/**
 * La modale d'avertissement d'environnement de test (R15).
 *
 * ────────────────────────────────────────────────────────────────────────────
 * POURQUOI ELLE EST ICI ET LE BANDEAU AILLEURS
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Le bandeau est un fragment HTML autonome, posé dans `index.html` hors du
 * nœud racine de React et rendu au démarrage du conteneur : il doit tenir même
 * si l'application ne démarre pas. La modale, elle, a besoin de choses que
 * seul le produit connaît — le nom du théâtre, qui vient de l'API — et de
 * l'accessibilité du composant `Modal` maison. Elle vit donc dans React.
 *
 * Le lien entre les deux est le bouton `.uat-more` du fragment : ce composant
 * s'y abonne au montage. S'il n'y a pas de bandeau — toute la production, et
 * l'UAT si quelqu'un oubliait `APP_ENV` — ce composant ne rend rien et ne
 * s'abonne à rien.
 *
 * ★ `sessionStorage`, jamais `localStorage` : la convention est explicite.
 *   Avec `localStorage`, « j'ai compris » deviendrait « ne me le redis jamais »
 *   et la règle serait vidée de son sens sans que personne ne le remarque.
 *   Une nouvelle session repart sur une modale qui s'ouvre.
 *
 * Source de vérité : docs.infra.ilygo.ch/conventions/bandeau-uat/
 */

import { useEffect, useState } from 'react'

import { useTheatrePublique } from '../../lib/theatre'
import { adresseSansTheatre } from '../accueil-theatre/racine'
import { Button, Modal } from '../../ui'

/**
 * ★ LE NOM DU PRODUIT, quand aucun théâtre n'est desservi.
 *
 * Sur l'apex du domaine — la vitrine, l'inscription, la documentation — il n'y
 * a pas de théâtre à nommer, et `useTheatre()` rendait alors son libellé de
 * repli : « Théâtre ». La modale annonçait donc « Vous utilisez la version de
 * test de « Théâtre » », ce qui ne désigne rien. Le produit s'appelle ILYGO
 * Billetterie, et c'est lui qu'on essaie à cette adresse.
 */
const NOM_PRODUIT = 'ILYGO Billetterie'

/**
 * ★★ L'ADRESSE DE PRODUCTION D'UN THÉÂTRE N'EST PAS CELLE DE LA PLATEFORME.
 *
 * `PUBLIC_PROD_URL` est posé par la CI pour toute la pile : c'est la racine du
 * produit en production (`https://ticket.ilygo.ch`). Depuis l'adresse de
 * recette d'un THÉÂTRE, la modale y renvoyait telle quelle — c'est-à-dire vers
 * la page de vente du logiciel, servie à l'équipe d'une salle qui cherchait sa
 * propre billetterie.
 *
 * Le sous-domaine courant EST l'identifiant du théâtre (`<slug>.<domaine>`) :
 * on le repique et on le pose sur l'hôte de production. Sans sous-domaine
 * (développement, instance mono-théâtre), rien à faire.
 */
function urlProductionDuTheatre(prodUrl: string, hote: string): string {
  if (prodUrl === '') return ''
  const etiquettes = hote.toLowerCase().split('.')
  const slug = etiquettes[0]
  if (etiquettes.length < 3 || slug === undefined || slug === '') return prodUrl
  try {
    const cible = new URL(prodUrl)
    cible.hostname = `${slug}.${cible.hostname}`
    return cible.toString().replace(/\/$/u, '')
  } catch {
    return prodUrl
  }
}

/** Clé de session : la modale s'ouvre une fois par session, pas à chaque page. */
const CLE_VU = 'ilygo-uat-ack'

interface InfosBandeau {
  readonly version: string
  readonly deploiement: string
  readonly prodUrl: string
  readonly mailpitUrl: string
}

function lireBandeau(): InfosBandeau | null {
  if (typeof document === 'undefined') return null
  const el = document.getElementById('ilygo-uat-banner')
  if (el === null) return null
  return {
    version: el.dataset['version'] ?? 'inconnue',
    deploiement: el.dataset['deploiement'] ?? '',
    prodUrl: el.dataset['prodUrl'] ?? '',
    mailpitUrl: el.dataset['mailpitUrl'] ?? '',
  }
}

/**
 * ★ Deux composants, et non un seul avec un `return null` tardif.
 *
 * L'enveloppe ne consomme qu'un `useState` : hors UAT — c'est-à-dire en
 * production, sur toutes les pages, pour tous les visiteurs — elle s'arrête
 * là. Aucun `useQuery`, donc aucune dépendance à un `QueryClientProvider`, et
 * pas une requête de plus. La règle des hooks interdisant de sauter un appel,
 * c'est la seule façon de rendre ce composant vraiment gratuit là où il n'a
 * rien à faire.
 */
export function ModaleUat() {
  const [infos] = useState<InfosBandeau | null>(() => lireBandeau())
  if (infos === null) return null
  return <ModaleUatInterne infos={infos} />
}

function ModaleUatInterne({ infos }: { readonly infos: InfosBandeau }) {
  const [ouverte, setOuverte] = useState(false)
  const theatre = useTheatrePublique()

  /* Un théâtre est desservi ici, ou bien on est sur l'apex — et le backend le
     dit par un `TENANT_INCONNU`, pas par un nom vide. */
  const surLApex = adresseSansTheatre(theatre.error)
  const nomTheatre = theatre.data?.nom
  const nom = surLApex || nomTheatre === undefined || nomTheatre === '' ? NOM_PRODUIT : nomTheatre
  const prodUrl = surLApex
    ? infos.prodUrl
    : urlProductionDuTheatre(infos.prodUrl, globalThis.location?.hostname ?? '')

  useEffect(() => {
    /* Première visite de la session : on l'ouvre sans qu'on la demande. Un
       avertissement qu'il faut aller chercher n'avertit personne. */
    try {
      if (sessionStorage.getItem(CLE_VU) === null) setOuverte(true)
    } catch {
      /* Stockage refusé (navigation privée stricte) : on ouvre quand même.
         Le défaut acceptable est de la montrer une fois de trop. */
      setOuverte(true)
    }

    const bouton = document.querySelector<HTMLButtonElement>('#ilygo-uat-banner .uat-more')
    if (bouton === null) return undefined
    const ouvrir = (): void => {
      setOuverte(true)
    }
    bouton.addEventListener('click', ouvrir)
    return () => {
      bouton.removeEventListener('click', ouvrir)
    }
  }, [infos])

  function fermer(): void {
    setOuverte(false)
    try {
      sessionStorage.setItem(CLE_VU, '1')
    } catch {
      /* Rien à faire : elle se rouvrira au prochain rendu de session. */
    }
  }

  return (
    <Modal
      ouvert={ouverte}
      onFermer={fermer}
      taille="md"
      titre={`Vous utilisez la version de test de « ${nom} »`}
      pied={<Button onClick={fermer}>J’ai compris</Button>}
    >
      <div className="flex flex-col gap-4 text-sm text-slate-700">
        <p>
          <b>Ce que c’est.</b> Une copie de « {nom} » destinée aux essais, aux démonstrations et à
          la recette. Elle porte des fonctionnalités non finalisées et peut se comporter autrement
          que la version en service.
          {prodUrl !== '' && (
            <>
              {' '}
              La version en service est ici :{' '}
              <a
                href={prodUrl}
                target="_blank"
                rel="noopener"
                className="underline underline-offset-2"
              >
                {prodUrl.replace(/^https?:\/\//u, '')}
              </a>
              .
            </>
          )}
        </p>

        <p>
          <b>N’y saisissez aucune donnée réelle.</b> Ni donnée de client, ni donnée de santé, ni
          dossier RH ou de candidature, ni pièce d’identité, ni coordonnée bancaire — et{' '}
          <b>jamais un mot de passe que vous utilisez ailleurs</b>. Traitez cet environnement comme
          s’il était public.
        </p>

        <p>
          <b>Rien n’est garanti dans la durée.</b> Ce que vous saisissez ici peut être modifié,
          réinitialisé ou supprimé à tout moment, sans préavis et sans restauration possible. Aucune
          sauvegarde ne vous est garantie. Les performances et la disponibilité ne le sont pas non
          plus.
        </p>

        {infos.mailpitUrl !== '' && (
          <p>
            <b>Les e-mails ne quittent pas la plateforme.</b> Confirmations d’inscription,
            invitations, factures, billets, liens de connexion : <b>tout</b> est capté par une boîte
            de test et <b>rien</b> n’atteint une vraie boîte aux lettres. Relevez vos messages ici :{' '}
            <a
              href={infos.mailpitUrl}
              target="_blank"
              rel="noopener"
              className="underline underline-offset-2"
            >
              boîte de test
            </a>
            .
          </p>
        )}

        <p>
          <b>Paiements de test.</b> Le théâtre peut utiliser le simulateur ou un prestataire en mode
          test. Utilisez uniquement des moyens de test ; ne réglez jamais une facture UAT par votre
          banque. La QR-facture, elle, est bien produite : c’est le document qu’on vérifie, pas un
          encaissement.
        </p>

        <p>
          <b>Un problème, une question ?</b> Écrivez à{' '}
          <a href="mailto:info@ilygo.ch" className="underline underline-offset-2">
            info@ilygo.ch
          </a>
          .
        </p>

        {/* Ce que l'on demandera au testeur quand il signalera un bug. */}
        <p className="border-t border-slate-200 pt-3 font-mono text-xs text-slate-500">
          Environnement uat · version {infos.version}
          {infos.deploiement !== '' && <> · déployée le {infos.deploiement}</>}
        </p>
      </div>
    </Modal>
  )
}
